import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getExams } from '../services/examService';
import { getAllExamTopicDetails } from '../services/topicService';
import { ArrowLeftIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import MultiExamComparisonChart from '../components/charts/MultiExamComparisonChart';

const CompareExams = () => {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [topicDetailsMap, setTopicDetailsMap] = useState({}); // examId -> topic details

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExams.length > 0) {
      fetchTopicDetails();
    }
  }, [selectedExams]);

  const fetchTopicDetails = async () => {
    try {
      const detailsMap = {};
      for (const examId of selectedExams) {
        try {
          const response = await getAllExamTopicDetails(examId);
          if (response.success && response.topicDetails) {
            detailsMap[examId] = response.topicDetails;
          }
        } catch (error) {
          console.log(`No topic details for exam ${examId}`);
        }
      }
      setTopicDetailsMap(detailsMap);
    } catch (error) {
      console.error('Error fetching topic details:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await getExams();
      setExams(response.exams);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExamToggle = (examId) => {
    setSelectedExams(prev => {
      if (prev.includes(examId)) {
        return prev.filter(id => id !== examId);
      } else {
        return [...prev, examId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedExams.length === exams.length) {
      setSelectedExams([]);
    } else {
      setSelectedExams(exams.map(exam => exam.id));
    }
  };

  const getSelectedExamsData = () => {
    return exams.filter(exam => selectedExams.includes(exam.id));
  };

  const calculateProgress = (selectedExamsData) => {
    if (selectedExamsData.length < 2) return null;

    const firstExam = selectedExamsData[0];
    const lastExam = selectedExamsData[selectedExamsData.length - 1];

    const subjects = [
      { key: 'turkish', name: 'Türkçe' },
      { key: 'math', name: 'Matematik' },
      { key: 'science', name: 'Fen' },
      { key: 'social', name: 'Sosyal' }
    ];

    if (user?.targetField === 'SAY') {
      subjects.push(
        { key: 'mathAyt', name: 'Matematik (AYT)' },
        { key: 'physics', name: 'Fizik' },
        { key: 'chemistry', name: 'Kimya' },
        { key: 'biology', name: 'Biyoloji' }
      );
    }

    const subjectChanges = subjects.map(subject => {
      const firstNet = firstExam.nets?.[subject.key] || 0;
      const lastNet = lastExam.nets?.[subject.key] || 0;
      const change = lastNet - firstNet;
      const changePercent = firstNet > 0 ? ((change / firstNet) * 100) : 0;

      // Tüm denemelerdeki bu dersin en iyi neti
      const bestNet = Math.max(...selectedExamsData.map(e => e.nets?.[subject.key] || 0));
      const allNets = selectedExamsData.map(e => e.nets?.[subject.key] || 0);
      const averageNet = allNets.reduce((a, b) => a + b, 0) / allNets.length;

      return {
        name: subject.name,
        change,
        changePercent,
        firstNet,
        lastNet,
        bestNet,
        averageNet
      };
    });

    const totalNetChange = lastExam.totalNet - firstExam.totalNet;
    const avgNetChange = totalNetChange / subjects.length;

    const mostImproved = subjectChanges.reduce((max, curr) => 
      curr.change > max.change ? curr : max
    , subjectChanges[0]);

    const mostDeclined = subjectChanges.reduce((min, curr) => 
      curr.change < min.change ? curr : min
    , subjectChanges[0]);

    // Calculate improvement rate (net per day)
    const daysBetween = Math.max(1, Math.ceil(
      (new Date(lastExam.examDate) - new Date(firstExam.examDate)) / (1000 * 60 * 60 * 24)
    ));
    const improvementRate = totalNetChange / daysBetween;

    // Best exam overall
    const bestExamOverall = selectedExamsData.reduce((best, curr) => 
      curr.totalNet > best.totalNet ? curr : best
    , selectedExamsData[0]);

    // Calculate consistency (standard deviation)
    const allTotalNets = selectedExamsData.map(e => e.totalNet);
    const avgTotalNet = allTotalNets.reduce((a, b) => a + b, 0) / allTotalNets.length;
    const variance = allTotalNets.reduce((sum, net) => sum + Math.pow(net - avgTotalNet, 2), 0) / allTotalNets.length;
    const stdDeviation = Math.sqrt(variance);
    const consistencyScore = Math.max(0, 100 - (stdDeviation / avgTotalNet * 100));

    return {
      subjectChanges,
      totalNetChange,
      avgNetChange,
      mostImproved,
      mostDeclined,
      firstExamDate: firstExam.examDate,
      lastExamDate: lastExam.examDate,
      scoreChange: (lastExam.estimatedScore || 0) - (firstExam.estimatedScore || 0),
      improvementRate,
      daysBetween,
      bestExamOverall,
      avgTotalNet,
      consistencyScore
    };
  };

  const selectedExamsData = getSelectedExamsData();
  const progress = calculateProgress(selectedExamsData);

  // Available subjects based on user's target field
  const getAvailableSubjects = () => {
    // TYT subjects with their backend subject mappings
    const subjects = [
      { 
        key: 'Türkçe', 
        name: 'Türkçe', 
        icon: '📖',
        backendSubjects: ['Türkçe', 'Edebiyat']
      },
      { 
        key: 'Matematik', 
        name: 'Matematik', 
        icon: '🔢',
        backendSubjects: ['Matematik']
      },
      { 
        key: 'Fen', 
        name: 'Fen Bilimleri', 
        icon: '🔬',
        backendSubjects: ['Fizik', 'Kimya', 'Biyoloji']
      },
      { 
        key: 'Sosyal', 
        name: 'Sosyal Bilimler', 
        icon: '🌍',
        backendSubjects: ['Tarih', 'Coğrafya', 'Felsefe', 'Din']
      }
    ];

    return subjects;
  };

  const availableSubjects = getAvailableSubjects();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Mobile Responsive */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Dashboard'a Dön
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mr-2 sm:mr-3 text-primary-600" />
                Deneme Karşılaştırma
              </h1>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600 mt-1">
                Denemelerini seç ve ilerleme analiz et
              </p>
            </div>
            <div className="text-xs sm:text-sm text-gray-500">
              {selectedExams.length} / {exams.length} deneme seçildi
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Sol Panel - Deneme Listesi - Mobile Responsive */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6 lg:sticky lg:top-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Denemeler</h2>
                <button
                  onClick={handleSelectAll}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {selectedExams.length === exams.length ? 'Hiçbirini Seçme' : 'Hepsini Seç'}
                </button>
              </div>

              {exams.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Henüz deneme eklemediniz</p>
                  <Link
                    to="/add-exam"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                  >
                    Deneme Ekle
                  </Link>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {exams.map((exam) => (
                    <label
                      key={exam.id}
                      className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition ${
                        selectedExams.includes(exam.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedExams.includes(exam.id)}
                        onChange={() => handleExamToggle(exam.id)}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(exam.examDate).toLocaleDateString('tr-TR')}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            exam.examType === 'TYT' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {exam.examType}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          Net: <span className="font-semibold">{exam.totalNet.toFixed(2)}</span>
                        </div>
                        {exam.estimatedScore && (
                          <div className="text-xs text-gray-500">
                            Puan: {exam.estimatedScore}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sağ Panel - Karşılaştırma */}
          <div className="lg:col-span-2 space-y-6">
            {selectedExams.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <ChartBarIcon className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Karşılaştırmak için deneme seç
                </h3>
                <p className="text-gray-600">
                  Sol taraftan en az 2 deneme seçerek karşılaştırma yapmaya başlayın
                </p>
              </div>
            ) : selectedExams.length === 1 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <ChartBarIcon className="w-20 h-20 text-yellow-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Bir deneme daha seç
                </h3>
                <p className="text-gray-600">
                  Karşılaştırma yapmak için en az 2 deneme seçmelisiniz
                </p>
              </div>
            ) : (
              <>
                {/* Genel Özet */}
                <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">📊 Genel Durum</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <p className="text-primary-100 text-sm">Toplam Net Değişimi</p>
                      <p className={`text-3xl font-bold ${progress.totalNetChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {progress.totalNetChange >= 0 ? '+' : ''}{progress.totalNetChange.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-primary-100 text-sm">Puan Değişimi</p>
                      <p className={`text-3xl font-bold ${progress.scoreChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {progress.scoreChange >= 0 ? '+' : ''}{progress.scoreChange.toFixed(2)}
                      </p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <p className="text-primary-100 text-sm">Günlük Ortalama İlerleme</p>
                      <p className={`text-2xl font-bold ${progress.improvementRate >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {progress.improvementRate >= 0 ? '+' : ''}{progress.improvementRate.toFixed(3)} net/gün
                      </p>
                      <p className="text-xs text-primary-200 mt-1">
                        {progress.daysBetween} gün içinde
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-primary-400">
                    <p className="text-sm text-primary-100">
                      {new Date(progress.firstExamDate).toLocaleDateString('tr-TR')} 
                      {' → '}
                      {new Date(progress.lastExamDate).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>

                {/* Konu Detayları Butonu */}
                {Object.keys(topicDetailsMap).length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <button
                      onClick={() => setShowTopicModal(true)}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-4 px-6 rounded-lg transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3"
                    >
                      <span className="text-2xl">🎯</span>
                      <span className="text-lg">Konu Detaylarını Görüntüle</span>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {Object.keys(topicDetailsMap).length} deneme
                      </span>
                    </button>
                  </div>
                )}

                {/* Performans Metrikleri */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* En İyi Performans */}
                  <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold">🏆 En İyi Performans</h3>
                    </div>
                    <p className="text-4xl font-bold mb-2">
                      {progress.bestExamOverall.totalNet.toFixed(2)}
                    </p>
                    <p className="text-sm opacity-90">
                      {new Date(progress.bestExamOverall.examDate).toLocaleDateString('tr-TR')}
                    </p>
                    {progress.bestExamOverall.estimatedScore && (
                      <p className="text-sm mt-2 bg-white/20 rounded px-2 py-1 inline-block">
                        Puan: {progress.bestExamOverall.estimatedScore.toFixed(2)}
                      </p>
                    )}
                  </div>

                  {/* Ortalama Performans */}
                  <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold">📊 Ortalama</h3>
                    </div>
                    <p className="text-4xl font-bold mb-2">
                      {progress.avgTotalNet.toFixed(2)}
                    </p>
                    <p className="text-sm opacity-90">
                      {selectedExams.length} deneme ortalaması
                    </p>
                  </div>

                  {/* Tutarlılık Skoru */}
                  <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold">🎯 Tutarlılık</h3>
                    </div>
                    <p className="text-4xl font-bold mb-2">
                      %{progress.consistencyScore.toFixed(0)}
                    </p>
                    <p className="text-sm opacity-90">
                      {progress.consistencyScore >= 80 ? 'Çok tutarlı!' : 
                       progress.consistencyScore >= 60 ? 'İyi tutarlılık' : 
                       'Daha tutarlı ol'}
                    </p>
                    <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                      <div 
                        className="h-2 bg-white rounded-full transition-all"
                        style={{ width: `${progress.consistencyScore}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* İlerleme/Gerileme Kartları */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* En Çok Gelişen */}
                  <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">🔼 En Çok Gelişen</h3>
                      <span className="text-2xl">🎯</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600 mb-2">
                      {progress.mostImproved.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {progress.mostImproved.firstNet.toFixed(2)} → {progress.mostImproved.lastNet.toFixed(2)}
                    </p>
                    <p className="text-lg font-semibold text-green-600 mt-2">
                      +{progress.mostImproved.change.toFixed(2)} net
                      {progress.mostImproved.changePercent > 0 && (
                        <span className="text-sm ml-2">
                          (+%{progress.mostImproved.changePercent.toFixed(1)})
                        </span>
                      )}
                    </p>
                  </div>

                  {/* En Çok Düşen */}
                  {progress.mostDeclined.change < 0 && (
                    <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">🔽 Dikkat Edilmeli</h3>
                        <span className="text-2xl">⚠️</span>
                      </div>
                      <p className="text-2xl font-bold text-red-600 mb-2">
                        {progress.mostDeclined.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {progress.mostDeclined.firstNet.toFixed(2)} → {progress.mostDeclined.lastNet.toFixed(2)}
                      </p>
                      <p className="text-lg font-semibold text-red-600 mt-2">
                        {progress.mostDeclined.change.toFixed(2)} net
                        {progress.mostDeclined.changePercent !== 0 && (
                          <span className="text-sm ml-2">
                            ({progress.mostDeclined.changePercent.toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Hızlı Özet Kartları */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* En Çok Gelişen Ders */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-md p-6 border-2 border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-green-700">🚀 En Çok Gelişen</h3>
                      <span className="text-2xl">📈</span>
                    </div>
                    <p className="text-2xl font-bold text-green-900 mb-1">
                      {progress.mostImproved.name}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <span className="font-semibold">
                        {progress.mostImproved.firstNet.toFixed(1)} → {progress.mostImproved.lastNet.toFixed(1)}
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <span className="text-3xl font-bold text-green-600">
                        +{progress.mostImproved.change.toFixed(2)}
                      </span>
                      <span className="text-sm text-green-700 ml-2">net artış</span>
                    </div>
                  </div>

                  {/* Ortalama İlerleme */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-md p-6 border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-blue-700">📊 Ortalama İlerleme</h3>
                      <span className="text-2xl">⚡</span>
                    </div>
                    <p className="text-lg font-semibold text-blue-900 mb-1">
                      Ders Başına
                    </p>
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <span>
                        {progress.subjectChanges.length} ders analiz edildi
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <span className={`text-3xl font-bold ${
                        progress.avgNetChange >= 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {progress.avgNetChange >= 0 ? '+' : ''}{progress.avgNetChange.toFixed(2)}
                      </span>
                      <span className="text-sm text-blue-700 ml-2">net ortalama</span>
                    </div>
                  </div>

                  {/* Dikkat Edilmesi Gereken */}
                  {progress.mostDeclined.change < 0 ? (
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl shadow-md p-6 border-2 border-orange-200">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-orange-700">⚠️ Dikkat Et</h3>
                        <span className="text-2xl">📉</span>
                      </div>
                      <p className="text-2xl font-bold text-orange-900 mb-1">
                        {progress.mostDeclined.name}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-orange-700">
                        <span className="font-semibold">
                          {progress.mostDeclined.firstNet.toFixed(1)} → {progress.mostDeclined.lastNet.toFixed(1)}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <span className="text-3xl font-bold text-red-600">
                          {progress.mostDeclined.change.toFixed(2)}
                        </span>
                        <span className="text-sm text-orange-700 ml-2">net düşüş</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-md p-6 border-2 border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-purple-700">🎯 Süper!</h3>
                        <span className="text-2xl">🎉</span>
                      </div>
                      <p className="text-xl font-bold text-purple-900 mb-1">
                        Tüm Derslerde İlerleme
                      </p>
                      <div className="flex items-center gap-2 text-sm text-purple-700 mb-3">
                        <span>Hiçbir derste gerileme yok!</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <span className="text-2xl font-bold text-purple-600">
                          ✨ Harika gidiyorsun!
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Detaylı Ders Analizi */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">📚 Ders Bazında Detaylı Analiz</h3>
                  <div className="space-y-4">
                    {progress.subjectChanges.map((subject, index) => (
                      <div key={index} className="p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">
                              {index === 0 ? '📖' : index === 1 ? '🔢' : index === 2 ? '🔬' : '🌍'}
                            </span>
                            <div>
                              <h4 className="font-bold text-gray-900 text-lg">{subject.name}</h4>
                              <p className="text-xs text-gray-500">
                                Ortalama: {subject.averageNet.toFixed(2)} • En İyi: {subject.bestNet.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-2xl font-bold ${
                              subject.change > 0 ? 'text-green-600' : 
                              subject.change < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {subject.change > 0 ? '+' : ''}{subject.change.toFixed(2)}
                            </span>
                            {subject.changePercent !== 0 && (
                              <p className={`text-sm font-semibold ${
                                subject.change > 0 ? 'text-green-600' : 
                                subject.change < 0 ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {subject.change > 0 ? '↗' : subject.change < 0 ? '↘' : '→'} 
                                {subject.changePercent > 0 ? '+' : ''}
                                %{Math.abs(subject.changePercent).toFixed(1)}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* İlerleme Göstergesi */}
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="bg-blue-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-blue-600 font-semibold">İlk</p>
                            <p className="text-lg font-bold text-blue-900">{subject.firstNet.toFixed(2)}</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-purple-600 font-semibold">Ortalama</p>
                            <p className="text-lg font-bold text-purple-900">{subject.averageNet.toFixed(2)}</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-green-600 font-semibold">Son</p>
                            <p className="text-lg font-bold text-green-900">{subject.lastNet.toFixed(2)}</p>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="relative">
                          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-500 ${
                                subject.change > 0 ? 'bg-gradient-to-r from-green-400 to-green-600' : 
                                subject.change < 0 ? 'bg-gradient-to-r from-red-400 to-red-600' : 'bg-gray-400'
                              }`}
                              style={{ 
                                width: `${Math.min(Math.abs(subject.changePercent), 100)}%` 
                              }}
                            />
                          </div>
                          {/* En iyi net göstergesi */}
                          {subject.bestNet > subject.lastNet && (
                            <div className="mt-2 text-xs text-yellow-600 flex items-center">
                              <span className="mr-1">⭐</span>
                              <span>En iyi performansın: {subject.bestNet.toFixed(2)} net</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hedef Karşılaştırması */}
                {user?.targetNets && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">🎯 Hedef Net Durumu</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Son denemenizin hedef netlerinize olan uzaklığı
                    </p>
                    <div className="space-y-3">
                      {Object.entries(user.targetNets.tyt).map(([key, targetNet]) => {
                        const lastExam = selectedExamsData[selectedExamsData.length - 1];
                        const currentNet = lastExam.nets?.[key] || 0;
                        const difference = currentNet - targetNet;
                        const percentage = targetNet > 0 ? (currentNet / targetNet) * 100 : 0;

                        const subjectNames = {
                          turkish: 'Türkçe',
                          math: 'Matematik',
                          science: 'Fen',
                          social: 'Sosyal'
                        };

                        return (
                          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold text-gray-900">{subjectNames[key]}</span>
                                <span className={`text-sm font-bold ${
                                  difference >= 0 ? 'text-green-600' : 'text-orange-600'
                                }`}>
                                  {currentNet.toFixed(2)} / {targetNet.toFixed(2)}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all ${
                                    percentage >= 100 ? 'bg-green-500' :
                                    percentage >= 80 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                />
                              </div>
                            </div>
                            <span className={`ml-4 text-xs font-semibold ${
                              difference >= 0 ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {difference >= 0 ? '✅' : '⏳'} 
                              {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Topic Details Modal */}
      {showTopicModal && (
        <TopicDetailsModal
          isOpen={showTopicModal}
          onClose={() => {
            setShowTopicModal(false);
            setSelectedSubject(null);
          }}
          selectedExamsData={selectedExamsData}
          topicDetailsMap={topicDetailsMap}
          availableSubjects={availableSubjects}
          selectedSubject={selectedSubject}
          setSelectedSubject={setSelectedSubject}
        />
      )}
    </div>
  );
};

// Topic Details Modal Component
const TopicDetailsModal = ({ 
  isOpen, 
  onClose, 
  selectedExamsData, 
  topicDetailsMap, 
  availableSubjects,
  selectedSubject,
  setSelectedSubject
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <span className="text-3xl">🎯</span>
                Konu Bazlı Analiz
              </h2>
              <p className="text-primary-100 mt-1">
                {selectedExamsData.length} deneme karşılaştırması
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Subject Selection */}
        {!selectedSubject && (
          <div className="p-8 flex-1 overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Hangi dersi analiz etmek istiyorsun?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {availableSubjects.map((subject) => {
                // Check if any backend subject has data
                const hasData = Object.values(topicDetailsMap).some(details =>
                  details.some(d => subject.backendSubjects.includes(d.subject))
                );

                return (
                  <button
                    key={subject.key}
                    onClick={() => hasData ? setSelectedSubject(subject.key) : null}
                    disabled={!hasData}
                    className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      hasData
                        ? 'border-primary-300 bg-gradient-to-br from-primary-50 to-white hover:border-primary-500 hover:shadow-lg cursor-pointer'
                        : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="text-5xl mb-3">{subject.icon}</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{subject.name}</h4>
                    {hasData ? (
                      <span className="text-sm text-green-600 font-medium">
                        ✓ Veri mevcut
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">
                        Veri yok
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Topic Comparison Content */}
        {selectedSubject && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Back Button */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedSubject(null)}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Ders Seçimine Dön
              </button>
              <h3 className="text-lg font-bold text-gray-900 mt-2 flex items-center gap-2">
                {availableSubjects.find(s => s.key === selectedSubject)?.icon}
                {availableSubjects.find(s => s.key === selectedSubject)?.name} - Konu Analizi
              </h3>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <TopicComparisonContent
                selectedExamsData={selectedExamsData}
                topicDetailsMap={topicDetailsMap}
                subject={availableSubjects.find(s => s.key === selectedSubject)?.name || selectedSubject}
                backendSubjects={availableSubjects.find(s => s.key === selectedSubject)?.backendSubjects || [selectedSubject]}
              />
            </div>
          </div>
        )}

        {/* Modal Footer */}
        {!selectedSubject && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition"
            >
              Kapat
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Topic Card Component - Reusable topic display
const TopicCard = ({ topic, improvement, lastNet, selectedExamsData, showSubjectBadge }) => {
  return (
    <div
      className={`border-2 rounded-lg p-4 transition ${
        improvement < 0 ? 'border-red-200 bg-red-50' :
        improvement > 0 ? 'border-green-200 bg-green-50' :
        'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-gray-900">{topic.topicName}</h4>
          {showSubjectBadge && topic.subject && (
            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
              {topic.subject}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {improvement !== 0 && (
            <span className={`text-sm font-medium ${
              improvement > 0 ? 'text-green-700' : 'text-red-700'
            }`}>
              {improvement > 0 ? '+' : ''}{improvement.toFixed(2)} net
              {topic.improvementPercent !== 0 && (
                <span className="ml-1 text-xs">
                  ({improvement > 0 ? '+' : ''}{topic.improvementPercent.toFixed(0)}%)
                </span>
              )}
            </span>
          )}
          <span className={`text-lg font-bold ${
            lastNet >= 2.5 ? 'text-green-600' :
            lastNet >= 1.5 ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {lastNet.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Progress across exams */}
      <div className="flex items-center gap-2 mt-3">
        {selectedExamsData
          .sort((a, b) => new Date(a.examDate) - new Date(b.examDate))
          .map((exam, index) => {
            const examTopic = topic.exams[exam.id];
            if (!examTopic) return null;

            const net = examTopic.net || 0;
            const successRate = examTopic.total > 0 ? (net / examTopic.total) * 100 : 0;

            return (
              <div key={exam.id} className="flex-1">
                <div className="text-xs text-gray-600 font-semibold mb-1">
                  {new Date(exam.examDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        successRate >= 80 ? 'bg-green-500' :
                        successRate >= 60 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(successRate, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs font-medium text-gray-700 mt-1 text-center">
                    {net.toFixed(1)}
                  </div>
                </div>
                {index < selectedExamsData.length - 1 && (
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                    →
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

// Helper component for topic comparison
const TopicComparisonContent = ({ selectedExamsData, topicDetailsMap, subject, backendSubjects }) => {
  if (Object.keys(topicDetailsMap).length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg mb-2">Seçili denemelerde {subject} konu detayı bulunamadı</p>
        <p className="text-gray-400 text-sm">
          Deneme eklerken veya düzenlerken {subject} konu detayları ekleyebilirsiniz
        </p>
      </div>
    );
  }

  // Get all unique topics from all exams for the selected subject(s)
  const allTopics = new Map();
  
  Object.entries(topicDetailsMap).forEach(([examId, details]) => {
    // Filter by backend subjects (e.g., for "Fen" we check Fizik, Kimya, Biyoloji)
    const relevantDetails = details.filter(d => backendSubjects.includes(d.subject));
    
    relevantDetails.forEach(subjectDetail => {
      if (subjectDetail.topicsWithNets) {
        subjectDetail.topicsWithNets.forEach(topic => {
          const uniqueKey = `${subjectDetail.subject}-${topic.topicName}`;
          
          if (!allTopics.has(uniqueKey)) {
            allTopics.set(uniqueKey, {
              topicId: topic.topicId,
              topicName: topic.topicName,
              subject: subjectDetail.subject, // Backend subject (Fizik, Kimya, etc.)
              exams: {}
            });
          }
          allTopics.get(uniqueKey).exams[examId] = topic;
        });
      }
    });
  });

  const topicsArray = Array.from(allTopics.values());

  // Calculate improvement for each topic
  const topicsWithImprovement = topicsArray.map(topic => {
    const examIds = Object.keys(topic.exams).sort((a, b) => {
      const examA = selectedExamsData.find(e => e.id === a);
      const examB = selectedExamsData.find(e => e.id === b);
      return new Date(examA.examDate) - new Date(examB.examDate);
    });

    if (examIds.length >= 2) {
      const firstExamId = examIds[0];
      const lastExamId = examIds[examIds.length - 1];
      const firstNet = topic.exams[firstExamId]?.net || 0;
      const lastNet = topic.exams[lastExamId]?.net || 0;
      const improvement = lastNet - firstNet;
      const improvementPercent = firstNet > 0 ? ((improvement / firstNet) * 100) : 0;

      return {
        ...topic,
        improvement,
        improvementPercent,
        firstNet,
        lastNet
      };
    }

    return { ...topic, improvement: 0, improvementPercent: 0 };
  });

  // Sort by improvement (worst to best)
  const sortedTopics = [...topicsWithImprovement].sort((a, b) => a.improvement - b.improvement);

  // Group topics by backend subject if there are multiple subjects
  const topicsBySubject = {};
  if (backendSubjects.length > 1) {
    backendSubjects.forEach(subject => {
      topicsBySubject[subject] = sortedTopics.filter(t => t.subject === subject);
    });
  }

  // Get weak topics (negative improvement or low nets)
  const weakTopics = sortedTopics.filter(t => t.improvement < 0 || t.lastNet < 2);
  const improvingTopics = sortedTopics.filter(t => t.improvement > 0);
  const stableTopics = sortedTopics.filter(t => t.improvement === 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Zayıf Konular</p>
              <p className="text-3xl font-bold text-red-700">{weakTopics.length}</p>
            </div>
            <span className="text-4xl">⚠️</span>
          </div>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">İyileşen Konular</p>
              <p className="text-3xl font-bold text-green-700">{improvingTopics.length}</p>
            </div>
            <span className="text-4xl">📈</span>
          </div>
        </div>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Sabit Konular</p>
              <p className="text-3xl font-bold text-blue-700">{stableTopics.length}</p>
            </div>
            <span className="text-4xl">➖</span>
          </div>
        </div>
      </div>

      {/* Topic List */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Konu Bazlı İlerleme</h3>
        
        {backendSubjects.length > 1 ? (
          // Grouped by subject when multiple subjects
          backendSubjects.map(backendSubject => {
            const subjectTopics = topicsBySubject[backendSubject] || [];
            if (subjectTopics.length === 0) return null;

            return (
              <div key={backendSubject} className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-md font-bold text-primary-700">{backendSubject}</h4>
                  <span className="text-sm text-gray-500">({subjectTopics.length} konu)</span>
                </div>
                
                {subjectTopics.map((topic) => {
                  const improvement = topic.improvement || 0;
                  const lastNet = topic.lastNet || 0;
                  
                  return (
                    <TopicCard
                      key={`${backendSubject}-${topic.topicId}`}
                      topic={topic}
                      improvement={improvement}
                      lastNet={lastNet}
                      selectedExamsData={selectedExamsData}
                      showSubjectBadge={false}
                    />
                  );
                })}
              </div>
            );
          })
        ) : (
          // Single subject - no grouping needed
          sortedTopics.map((topic) => {
            const improvement = topic.improvement || 0;
            const lastNet = topic.lastNet || 0;
            
            return (
              <TopicCard
                key={topic.topicId}
                topic={topic}
                improvement={improvement}
                lastNet={lastNet}
                selectedExamsData={selectedExamsData}
                showSubjectBadge={false}
              />
            );
          })
        )}
      </div>

      {/* Recommendations */}
      {weakTopics.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <h4 className="font-bold text-yellow-900 mb-2">💡 Öneri: Şu konulara odaklan</h4>
          <div className="flex flex-wrap gap-2">
            {weakTopics.slice(0, 5).map(topic => (
              <span
                key={topic.topicId}
                className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium"
              >
                {topic.topicName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareExams;
